import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, CheckCircle2, XCircle, PlayCircle, Plus, Calendar, Users, AlertTriangle, Loader2, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CutoverStudio = ({ project }) => {
  const [cutoverPlans, setCutoverPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activities, setActivities] = useState([]);
  const [rehearsals, setRehearsals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [showCreateActivity, setShowCreateActivity] = useState(false);
  const [generatingActivities, setGeneratingActivities] = useState(false);

  useEffect(() => {
    fetchCutoverPlans();
  }, [project.id]);

  useEffect(() => {
    if (selectedPlan) {
      fetchActivities(selectedPlan.id);
      fetchRehearsals(selectedPlan.id);
    }
  }, [selectedPlan]);

  const fetchCutoverPlans = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/cutover-plans`);
      setCutoverPlans(response.data);
      if (response.data.length > 0 && !selectedPlan) {
        setSelectedPlan(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching cutover plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async (planId) => {
    try {
      const response = await axios.get(`${API}/cutover-plans/${planId}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchRehearsals = async (planId) => {
    try {
      const response = await axios.get(`${API}/cutover-plans/${planId}/rehearsals`);
      setRehearsals(response.data);
    } catch (error) {
      console.error('Error fetching rehearsals:', error);
    }
  };

  const createPlan = async (planData) => {
    try {
      const newPlan = {
        ...planData,
        project_id: project.id,
        cutover_date: new Date(planData.cutover_date).toISOString(),
      };
      const response = await axios.post(`${API}/cutover-plans`, newPlan);
      await fetchCutoverPlans();
      setSelectedPlan(response.data);
      setShowCreatePlan(false);
    } catch (error) {
      console.error('Error creating plan:', error);
    }
  };

  const generateActivities = async () => {
    if (!selectedPlan) return;
    setGeneratingActivities(true);
    try {
      await axios.post(`${API}/cutover-plans/${selectedPlan.id}/generate-activities`);
      await fetchActivities(selectedPlan.id);
    } catch (error) {
      console.error('Error generating activities:', error);
    } finally {
      setGeneratingActivities(false);
    }
  };

  const createActivity = async (activityData) => {
    try {
      const newActivity = {
        ...activityData,
        cutover_plan_id: selectedPlan.id,
        project_id: project.id,
      };
      await axios.post(`${API}/cutover-activities`, newActivity);
      await fetchActivities(selectedPlan.id);
      setShowCreateActivity(false);
    } catch (error) {
      console.error('Error creating activity:', error);
    }
  };

  const updateActivityStatus = async (activityId, newStatus) => {
    try {
      const activity = activities.find(a => a.id === activityId);
      await axios.put(`${API}/cutover-activities/${activityId}`, {
        ...activity,
        status: newStatus
      });
      await fetchActivities(selectedPlan.id);
    } catch (error) {
      console.error('Error updating activity:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getActivityTypeColor = (type) => {
    switch (type) {
      case 'preparation': return 'bg-blue-50 border-blue-200';
      case 'execution': return 'bg-purple-50 border-purple-200';
      case 'validation': return 'bg-green-50 border-green-200';
      case 'rollback': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Cutover Studio</h2>
            <p className="text-gray-600">Plan and execute your go-live cutover with confidence</p>
          </div>
          <button
            onClick={() => setShowCreatePlan(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Cutover Plan</span>
          </button>
        </div>
      </div>

      {cutoverPlans.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-100">
          <Calendar className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Cutover Plans Yet</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">Create your first cutover plan to start planning your go-live activities</p>
          <button
            onClick={() => setShowCreatePlan(true)}
            className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create Cutover Plan</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Plan Selector */}
          <div className="lg:col-span-1 space-y-4">
            {cutoverPlans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan)}
                className={`bg-white rounded-xl p-4 cursor-pointer border-2 transition-all ${
                  selectedPlan?.id === plan.id
                    ? 'border-blue-500 shadow-lg'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <h3 className="font-semibold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(plan.status)}`}>
                    {plan.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {plan.rehearsal_count} rehearsals
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Plan Details */}
          <div className="lg:col-span-2 space-y-6">
            {selectedPlan && (
              <>
                {/* Plan Overview */}
                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Plan Overview</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-500">Cutover Date</p>
                        <p className="font-semibold text-gray-900">
                          {new Date(selectedPlan.cutover_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <PlayCircle className="w-8 h-8 text-purple-600" />
                      <div>
                        <p className="text-xs text-gray-500">Rehearsals</p>
                        <p className="font-semibold text-gray-900">{selectedPlan.rehearsal_count}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                      <div>
                        <p className="text-xs text-gray-500">Go-Live Ready</p>
                        <p className="font-semibold text-gray-900">
                          {selectedPlan.go_live_ready ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Users className="w-8 h-8 text-orange-600" />
                      <div>
                        <p className="text-xs text-gray-500">Activities</p>
                        <p className="font-semibold text-gray-900">{activities.length}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activities */}
                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Cutover Activities</h3>
                    <div className="flex space-x-3">
                      <button
                        onClick={generateActivities}
                        disabled={generatingActivities}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50"
                      >
                        {generatingActivities ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Generating...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            <span className="text-sm">AI Generate</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowCreateActivity(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-sm">Add Activity</span>
                      </button>
                    </div>
                  </div>

                  {activities.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600">No activities yet. Use AI to generate or add manually.</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {activities.map((activity) => (
                        <div
                          key={activity.id}
                          className={`border-2 rounded-lg p-4 ${getActivityTypeColor(activity.activity_type)}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <span className="font-mono text-sm font-semibold text-gray-700">
                                  #{activity.sequence}
                                </span>
                                <h4 className="font-semibold text-gray-900">{activity.title}</h4>
                                {activity.is_critical && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-semibold">
                                    CRITICAL
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-3">{activity.description}</p>
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <span className="flex items-center space-x-1">
                                  <Users className="w-3 h-3" />
                                  <span>{activity.assigned_team}</span>
                                </span>
                                <span className="flex items-center space-x-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{activity.estimated_duration_minutes} min</span>
                                </span>
                                <span className="capitalize px-2 py-0.5 bg-white rounded-full">
                                  {activity.activity_type}
                                </span>
                              </div>
                            </div>
                            <select
                              value={activity.status}
                              onChange={(e) => updateActivityStatus(activity.id, e.target.value)}
                              className={`ml-4 px-3 py-1 rounded-lg border-2 text-sm font-semibold ${getStatusColor(activity.status)}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="failed">Failed</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Rehearsals */}
                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Rehearsal History</h3>
                  {rehearsals.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-600">No rehearsals recorded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {rehearsals.map((rehearsal) => (
                        <div key={rehearsal.id} className="border-2 border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-gray-900">
                              Rehearsal #{rehearsal.rehearsal_number}
                            </h4>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(rehearsal.status)}`}>
                              {rehearsal.status}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Date</p>
                              <p className="font-semibold">
                                {new Date(rehearsal.rehearsal_date).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Duration</p>
                              <p className="font-semibold">{rehearsal.duration_minutes} min</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Success Rate</p>
                              <p className="font-semibold">{rehearsal.success_rate}%</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Plan Modal */}
      {showCreatePlan && (
        <CreatePlanModal
          onClose={() => setShowCreatePlan(false)}
          onCreate={createPlan}
        />
      )}

      {/* Create Activity Modal */}
      {showCreateActivity && selectedPlan && (
        <CreateActivityModal
          onClose={() => setShowCreateActivity(false)}
          onCreate={createActivity}
          nextSequence={activities.length + 1}
        />
      )}
    </div>
  );
};

// Create Plan Modal
const CreatePlanModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cutover_date: '',
    status: 'planning'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Create Cutover Plan</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Plan Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Production Cutover Plan"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Describe the cutover plan"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Cutover Date *</label>
            <input
              type="date"
              required
              value={formData.cutover_date}
              onChange={(e) => setFormData({ ...formData, cutover_date: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Create Plan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Activity Modal
const CreateActivityModal = ({ onClose, onCreate, nextSequence }) => {
  const [formData, setFormData] = useState({
    sequence: nextSequence,
    title: '',
    description: '',
    activity_type: 'execution',
    assigned_team: 'Migration Team',
    estimated_duration_minutes: 60,
    is_critical: false,
    status: 'pending',
    dependencies: [],
    rollback_procedure: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Create Cutover Activity</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sequence #</label>
              <input
                type="number"
                value={formData.sequence}
                onChange={(e) => setFormData({ ...formData, sequence: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Duration (min)</label>
              <input
                type="number"
                value={formData.estimated_duration_minutes}
                onChange={(e) => setFormData({ ...formData, estimated_duration_minutes: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Activity Title *</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Freeze production system"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Activity Type</label>
              <select
                value={formData.activity_type}
                onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="preparation">Preparation</option>
                <option value="execution">Execution</option>
                <option value="validation">Validation</option>
                <option value="rollback">Rollback</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Assigned Team</label>
              <input
                type="text"
                value={formData.assigned_team}
                onChange={(e) => setFormData({ ...formData, assigned_team: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_critical"
              checked={formData.is_critical}
              onChange={(e) => setFormData({ ...formData, is_critical: e.target.checked })}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="is_critical" className="text-sm font-semibold text-gray-700">
              Mark as Critical Activity
            </label>
          </div>
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Create Activity
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CutoverStudio;